import { customRequest } from '@/shared/utils'
import { computeParams } from '@/shared/utils/to-params'
import type { IUserDetail } from './model'

const BASE = '/api/v1/admin/pools/sub_account'

export function getUsersList(query?: { search?: string; page?: number; page_size?: number }) {
    return customRequest
        .getItem<any, any>(`${BASE}/users_list` + computeParams(query ?? {}))
        .then((res) => res.data as { count: number; results: IUserDetail[] })
}

// ─── Reference / filter sources ─────────────────────────────────────
export function getUsers(query?: { search?: string; limit?: number }) {
    return customRequest
        .getItem<any, any>(`${BASE}/users` + computeParams(query ?? {}))
        .then((res) => res.data)
}

export function getSubAccounts(query?: {
    search?: string
    user_id?: number
    pool?: string
    limit?: number
}) {
    return customRequest
        .getItem<any, any>(`${BASE}/sub_accounts` + computeParams(query ?? {}))
        .then((res) => res.data)
}

export function getPools() {
    return customRequest
        .getItem<any, any>(`${BASE}/pools`)
        .then((res) => res.data)
}

// ─── Main data endpoints ────────────────────────────────────────────
export interface SummariesQuery {
    user_id?: number
    sub_account_id?: number
    pool?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_type?: string
    export?: string
}

export function getSummaries(query: SummariesQuery) {
    return customRequest
        .getItem<any, any>(`${BASE}/summaries` + computeParams(query))
        .then((res) => {
            if (res.status === 202) {
                return { _export: true, ...res.data }
            }
            return res.data
        })
}

export interface EarningsQuery {
    start_date: string
    end_date: string
    user_id?: number
    sub_account_id?: number
    pool?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_type?: string
    export?: string
}

export function getEarnings(query: EarningsQuery) {
    return customRequest
        .getItem<any, any>(`${BASE}/earnings` + computeParams(query))
        .then((res) => {
            if (res.status === 202) {
                return { _export: true, ...res.data }
            }
            return res.data
        })
}

export interface PayoutsQuery {
    start_date: string
    end_date: string
    user_id?: number
    sub_account_id?: number
    pool?: string
    page?: number
    page_size?: number
    sort_by?: string
    sort_type?: string
    export?: string
}

export function getPayouts(query: PayoutsQuery) {
    return customRequest
        .getItem<any, any>(`${BASE}/payouts` + computeParams(query))
        .then((res) => {
            if (res.status === 202) {
                return { _export: true, ...res.data }
            }
            return res.data
        })
}

// ─── Earning Options Routing ─────────────────────────────────────────────────

export function getEarningSchemesCurrencies() {
    return customRequest
        .getItem<any, any>(`${BASE}/earning_scheme_currencies`)
        .then((res) => res.data)
}

export function getDefaultOptions(query?: { scheme_type?: string; currency?: string; pool_code?: string }) {
    return customRequest
        .getItem<any, any>(`${BASE}/earning_options/defaults` + computeParams(query ?? {}))
        .then((res) => res.data)
}

export function createDefaultOption(body: { earning_scheme_currency_id: number; commission?: number; is_active: boolean }) {
    return customRequest
        .post<any, any>(`${BASE}/earning_options/defaults`, body)
        .then((res: any) => res.data)
}

export function updateDefaultOption(id: number, body: Partial<{ commission: number | null; is_active: boolean }>) {
    return customRequest
        .patch<any, any>(`${BASE}/earning_options/defaults/${id}`, body)
        .then((res: any) => res.data)
}

export function deleteDefaultOption(id: number) {
    return customRequest
        .delete<any>(`${BASE}/earning_options/defaults/${id}`)
}

export function getUserEarningOptions(userId: number) {
    return customRequest
        .getItem<any, any>(`${BASE}/earning_options/users/${userId}`)
        .then((res) => res.data)
}

export function getEffectiveOptions(userId: number) {
    return customRequest
        .getItem<any, any>(`${BASE}/earning_options/users/${userId}/effective`)
        .then((res) => res.data)
}

export function createUserEarningOption(
    userId: number,
    body: { earning_scheme_currency_id: number; commission?: number; is_active?: boolean; is_hidden?: boolean },
) {
    return customRequest
        .post<any, any>(`${BASE}/earning_options/users/${userId}`, body)
        .then((res: any) => res.data)
}

export function updateUserEarningOption(
    userId: number,
    id: number,
    body: Partial<{ commission: number | null; is_active: boolean; is_hidden: boolean }>,
) {
    return customRequest
        .patch<any, any>(`${BASE}/earning_options/users/${userId}/${id}`, body)
        .then((res: any) => res.data)
}

export function deleteUserEarningOption(userId: number, id: number) {
    return customRequest
        .delete<any>(`${BASE}/earning_options/users/${userId}/${id}`)
}

export function getPoolsMeta() {
    return customRequest
        .getItem<any, any>(`${BASE}/pools_meta`)
        .then((res) => res.data)
}

export function updatePoolMeta(
    poolId: number,
    body: Partial<{ is_deprecated: boolean; deprecation_message: string | null; is_active: boolean }>,
) {
    return customRequest
        .patch<any, any>(`${BASE}/pools_meta/${poolId}`, body)
        .then((res: any) => res.data)
}

// ─── Async export ───────────────────────────────────────────────────
export interface ExportStatus {
    status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED'
    download_ready: boolean
    download_url?: string
}

export function getExportStatus(jobId: string) {
    return customRequest
        .getItem<any, any>(`${BASE}/exports/${jobId}`)
        .then((res) => res.data as ExportStatus)
}

export function downloadExport(jobId: string) {
    return customRequest
        .getItem<any, any>(`${BASE}/exports/${jobId}?download=1`, '', {
            responseType: 'blob',
        })
        .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `export_${jobId}.xlsx`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        })
}
